/**
 * Transforms TipTap HTML into email-safe HTML by adding inline styles
 * to elements that rely on class-based or tag-based styling.
 *
 * Called before sending to the API so that email forwarding
 * renders correctly. Does NOT affect in-app rendering —
 * SafeHtmlRenderer handles that separately via CSS-in-JS.
 *
 * Hardcoded hex values map to theme tokens:
 *   #E3E3E3 → neutral["015"]
 *   #383940 → lightBg.medium
 *   #D2DDFF → purple[200]
 *   #5563F5 → primary.main
 */
export function inlineEmailStyles(html: string): string {
  if (!html) return html;

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");

    // Blockquote — add border + padding inline
    doc.querySelectorAll("blockquote").forEach((el) => {
      el.setAttribute(
        "style",
        "border-left: 3px solid #E3E3E3; padding-left: 12px; margin: 4px 0; color: #383940;",
      );
    });

    // Highlight — replace <mark> with <span> for email client compatibility
    doc.querySelectorAll("mark").forEach((markEl) => {
      const span = doc.createElement("span");
      const existing = markEl.getAttribute("style") || "";
      span.setAttribute(
        "style",
        `${existing}${existing ? "; " : ""}border-radius: 2px; padding: 1px 2px;`,
      );
      while (markEl.firstChild) {
        span.appendChild(markEl.firstChild);
      }
      markEl.replaceWith(span);
    });

    // Mention chips — add inline styles (class is stripped by email clients)
    doc.querySelectorAll('span[data-type="mention"]').forEach((el) => {
      const existing = el.getAttribute("style") || "";
      el.setAttribute(
        "style",
        `${existing}${existing ? "; " : ""}background-color: #D2DDFF; color: #5563F5; border-radius: 4px; padding: 1px 6px; font-size: 12px; font-weight: 500;`,
      );
    });

    return doc.body.innerHTML;
  } catch {
    return html;
  }
}
